import { FLAGS } from '@iheartjane/feature-flags';
import { WizardBanner } from '@iheartjane/menu';
import { useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { MenuHeader } from '@jane/bloom/components/menuHeader/menuHeader';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { CategoryTab } from '@jane/bloom/components/routes/category/components/categoryTab';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRemoveWizardBannerAbTest } from '@jane/bloom/hooks/useRemoveWizardBannerAbTest';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import type { FeaturedResults } from '@jane/bloom/loaders/routes/featured.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';

import { FeaturedTab } from './components/featuredTab';

export function FeaturedRouteComponent() {
  const { customSEO, featureFlags, metaProps, pinnedStore, theme } =
    useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const removeWizardBanner = useRemoveWizardBannerAbTest();
  const showWizardBanner = showInlineWizardCta && !removeWizardBanner;
  const loaderData = useLoaderData<FeaturedResults>();

  const rowInstance = useMemoizedRowInstance(
    loaderData.viewMode === 'category'
      ? loaderData.recommendedSortResults
      : undefined
  );

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: loaderData.menuTopRow,
    searchFilter: loaderData.searchFilterWithoutBrands,
    storeId: loaderData.storeId,
  });

  const totalProducts: number = useMemo(() => {
    if (rowInstance?.totalProducts) return rowInstance.totalProducts;
    if (
      loaderData.viewMode === 'featured' &&
      loaderData['customRowMenuProducts']
    ) {
      return Object.values(loaderData['customRowMenuProducts']).reduce(
        (acc, curr) => acc + curr.totalCount,
        0
      );
    }
    return 0;
  }, [rowInstance?.totalProducts, loaderData]);

  useEffect(() => {
    trackSearchedMenuProducts({
      hits: totalProducts ?? 0,
      searchCriteria: loaderData.searchCriteria,
      theme,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loaderData.searchCriteria), totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={loaderData.availableFilters}
      searchCriteria={loaderData.searchCriteria}
    >
      <SeoHeading
        customSEOPage={customSEO?.featured}
        defaultTitle={customSEO?.featured?.title}
        metaVariables={createMetaVariables(metaProps)}
      />

      <MenuHeader
        banners={{
          communicationBanner: loaderData.communicationBanner,
          desktopCarouselBanner: loaderData.desktopCarouselBanner,
          mobileCarouselBanner: loaderData.mobileCarouselBanner,
          showCarousel: loaderData.showCarousel,
          showImageBanner: loaderData.showImageBanner,
          storeName: pinnedStore?.name,
        }}
      />

      <BloomWizardModal
        brandSpecials={loaderData.brandSpecials}
        specials={loaderData.enabledSpecialsForRow}
      >
        {({ openModal }) => {
          const onWizardClick = removeWizardBanner
            ? () => openModal(WIZARD_SOURCE.BUTTON)
            : undefined;

          return (
            <>
              <div className="featured-filters-container">
                <FilterSelector
                  availableProductsCount={rowInstance?.totalProducts}
                  sortOptions={loaderData.sort.sortOptions}
                  currentSort={loaderData.sort.currentSort}
                  hasLinkedBank={loaderData.hasLinkedBank}
                  onWizardClick={onWizardClick}
                  shouldShowGold={loaderData.janeGold.shouldShowGold}
                  hasJaneGoldProducts={loaderData.janeGold.hasJaneGoldProducts}
                  showCompare={featureFlags[FLAGS.c1CompareProducts]}
                />
              </div>

              <MobileFilterRow
                onWizardClick={onWizardClick}
                shouldShowGold={loaderData.janeGold.shouldShowGold}
                availableProductsCount={rowInstance?.totalProducts}
                sortOptions={loaderData.sort.sortOptions}
                currentSort={loaderData.sort.currentSort}
                hasJaneGoldProducts={loaderData.janeGold.hasJaneGoldProducts}
              />

              {showWizardBanner && (
                <div className="wizard-banner-container">
                  <WizardBanner
                    onClickStart={() => openModal(WIZARD_SOURCE.BANNER)}
                  />
                </div>
              )}
            </>
          );
        }}
      </BloomWizardModal>

      {loaderData.viewMode === 'category' && (
        <CategoryTab
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={loaderData.brandSpecials}
          category={loaderData.category}
          customCategoryLabels={loaderData.customCategoryLabels}
          customLanguage={loaderData.customLanguage}
          featuredTab
          menuTopRowInstance={menuTopRowInstance}
          preloadedProductIds={loaderData.menuProductImgsAboveTheFold.map(
            ({ id }) => id
          )}
          rowInstance={rowInstance}
          specials={loaderData.enabledSpecialsForRow}
          janeGold={loaderData.janeGold}
          enabledSpecialsForRow={loaderData.enabledSpecialsForRow}
        />
      )}
      {loaderData.viewMode === 'featured' && (
        <FeaturedTab
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={loaderData.brandSpecials}
          complianceLanguage={loaderData.complianceLanguage}
          customLanguage={loaderData.customLanguage}
          featuredPlacements={loaderData.featuredPlacements}
          enabledSpecialsForRow={loaderData.enabledSpecialsForRow}
          menuTopRowInstance={menuTopRowInstance}
          janeGold={loaderData.janeGold}
          userHasLinkedBank={!!loaderData.aeropayUser?.bank_accounts?.length}
        />
      )}
    </BloomFilterProvider>
  );
}
